import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import HighchartWidget from "../../components/highchartWidget";
import {
  loadSharmaChartByCatalogKey,
  resolveSharmaFetchPeriod,
} from "../../services/sharmaReportHandlers";
import { formatSharmaChartLoadError } from "../../services/sharmaReportsApi";
import { isValidReportPeriodYyyyMm } from "../../services/sharmaReportValidation";
import { selectActivePortfolioApiId } from "../../redux/slices/portfolioSlice";
import { setChartTitle } from "../../redux/slices/uiSlice";

/**
 * Column / polar charts from Sharma report API (drawdowns, attribution, risk profile).
 * Expects chartData from adapters: { title, categories, series, extraOptions, loadError?, chartKind, reportChartCatalogKey? }
 */
export default function SharmaReportChartContent({
  chartData,
  setChartPayload,
}) {
  const dispatch = useDispatch();
  const activePortfolioApiId = useSelector(selectActivePortfolioApiId);
  const reportPeriodRaw = useSelector((s) => {
    const cd = s.portfolio?.contextData;
    return cd?.reportPeriod ?? cd?.perd ?? null;
  });
  const reportPeriod =
    typeof reportPeriodRaw === "string" &&
    isValidReportPeriodYyyyMm(reportPeriodRaw)
      ? reportPeriodRaw.trim()
      : null;

  const periodResolved = reportPeriod ?? resolveSharmaFetchPeriod();
  const skipRefetchBootstrap = useRef(true);
  const lastPortfolioRef = useRef(activePortfolioApiId);
  const lastPeriodRef = useRef(periodResolved);

  useEffect(() => {
    dispatch(setChartTitle(chartData?.title || ""));
    return () => dispatch(setChartTitle(""));
  }, [dispatch, chartData?.title]);

  useEffect(() => {
    const catalogKey = chartData?.reportChartCatalogKey;
    if (
      !catalogKey ||
      typeof setChartPayload !== "function" ||
      chartData?.loadError
    ) {
      return;
    }
    if (!activePortfolioApiId) {
      return;
    }

    if (skipRefetchBootstrap.current) {
      skipRefetchBootstrap.current = false;
      lastPortfolioRef.current = activePortfolioApiId;
      lastPeriodRef.current = periodResolved;
      return;
    }

    const portChanged = lastPortfolioRef.current !== activePortfolioApiId;
    const periodChanged = lastPeriodRef.current !== periodResolved;
    if (!portChanged && !periodChanged) {
      return;
    }

    lastPortfolioRef.current = activePortfolioApiId;
    lastPeriodRef.current = periodResolved;

    const ac = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const next = await loadSharmaChartByCatalogKey(
          catalogKey,
          activePortfolioApiId,
          ac.signal
        );
        if (cancelled) return;
        if (next) {
          setChartPayload(next);
        }
      } catch (e) {
        if (cancelled || ac.signal.aborted) return;
        setChartPayload({
          loadError: formatSharmaChartLoadError(e),
          title: chartData?.title,
          reportChartCatalogKey: catalogKey,
        });
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [
    activePortfolioApiId,
    periodResolved,
    chartData?.reportChartCatalogKey,
    chartData?.loadError,
    chartData?.title,
    setChartPayload,
  ]);

  if (!chartData) {
    return (
      <div className="chart-content-center">
        <p>No chart data</p>
      </div>
    );
  }

  if (chartData.loadError) {
    return (
      <div className="chart-content-center" role="alert">
        <p style={{ color: "#b91c1c", fontWeight: 600 }}>{chartData.loadError}</p>
      </div>
    );
  }

  const isPolar = chartData.chartKind === "polar";
  const chartType = isPolar ? "line" : "column";
  const hideCategoryAxisLabels =
    chartData.reportChartCatalogKey === "Drawdowns Over Time";
  const riskAttributionPresentation =
    chartData.reportChartCatalogKey === "Risk and Performance Attribution";
  const chartInstanceKey =
    chartData.reportChartCatalogKey ??
    chartData.chartKind ??
    chartData.title ??
    "chart";

  return (
    <div className="chart-content-center" style={{ width: "100%", maxWidth: 960 }}>
      <HighchartWidget
        key={chartInstanceKey}
        type={chartType}
        chartLayout={isPolar ? "polar" : "cartesian"}
        categories={chartData.categories || []}
        series={chartData.series || []}
        options={chartData.extraOptions || {}}
        height={420}
        hideCategoryAxisLabels={hideCategoryAxisLabels}
        riskAttributionPresentation={riskAttributionPresentation}
      />
    </div>
  );
}
